import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import chunk from "lodash/chunk";
import { useUploadFiles } from "@bn/api/pdfParser";
import InfoIcon from "@icons/myicons/warning.svg?react";
import { Banner, FileDropField } from "@components";
import { generateRandomString } from "@features/fileDrop/helpers/bookingLocator";
import { ParsingResultActivity, UniqueParsingResult } from "./ParsingResult";

export interface Props {
  onParsingResult: (parsingResults: UniqueParsingResult[]) => void;
  setIsParsingFinished: (isParsingFinished: boolean) => void;
  isValidating?: boolean;
}

export const FileDrop = ({
  onParsingResult,
  setIsParsingFinished,
  isValidating,
}: Props) => {
  const { t } = useTranslation();
  const [error, setError] = useState<null | string>(null);
  const [isParsing, setIsParsing] = useState(false);

  const { mutateAsync } = useUploadFiles();

  const handleDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    setError(null);
    setIsParsingFinished(false);
    setIsParsing(true);

    const chunks = chunk(acceptedFiles, 20);

    const promises = chunks.map(async (chunk) => {
      const formData = new FormData();
      const files: Record<string, File> = chunk.reduce(
        (acc, file) => ({
          ...acc,
          [generateRandomString()]: file,
        }),
        {}
      );

      Object.entries(files).forEach(([id, file]) => {
        formData.append("multipartFiles", file, id);
      });

      const data = await mutateAsync({
        multipartFiles: formData.getAll("multipartFiles") as File[],
      });

      const result = Object.entries(files).reduce((acc, [id, file]) => {
        const activities =
          data.activities?.reduce((acc, activity) => {
            if (activity.sourceFilename === id) {
              return [
                ...acc,
                {
                  parsingResultId: id,
                  ...activity,
                },
              ];
            }
            return acc;
          }, [] as ParsingResultActivity[]) || [];

        if (activities.length === 0) {
          setError(t("form.errors.pdfImport"));
        }

        return [
          ...acc,
          {
            id,
            file,
            activities,
            successful: activities.length > 0,
            error:
              activities.length === 0
                ? t("transactionUpload.SomethingWrong")
                : undefined,
          },
        ];
      }, [] as UniqueParsingResult[]);

      onParsingResult(result);
    });

    Promise.allSettled(promises)
      .catch(() => {
        setError(t("investments.errors.pdfImport"));
      })
      .finally(() => {
        setIsParsing(false);
        setIsParsingFinished(true);
      });
  };

  return (
    <>
      <FileDropField
        isParsing={Boolean(isParsing || isValidating)}
        onDrop={handleDrop}
        text={t("transactionUpload.dropOrSelectFile")}
      />
      {error && (
        <Banner type="warn" icon={<InfoIcon />} mb={3} mt={3} text={error} />
      )}
    </>
  );
};
